// file: ComprehensiveDocPanel.jsx
// Upload a comprehensive context document (txt/md/json) for use in block generation.
// The document is stored in Firebase Storage alongside the itinerary files and its
// URL is saved in itinerary.metadata.comprehensiveDocUrl.

import React, { useRef, useState } from 'react';
import { FileText, Upload, Trash2, CheckCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../../contexts/AuthContext';
import { getStoragePathComponents } from '../../utils/filenameUtils';
import { getFirebaseUploadAuthHeaders } from '../../../../utils/firebaseUploadAuth';

const UPLOAD_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendDocumentUploaderToFirebase';
const ACCEPTED = '.txt,.md,.json';

const ComprehensiveDocPanel = ({ itinerary, setItinerary }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState('');
  const { currentUser } = useAuth();

  const existingUrl = itinerary?.metadata?.comprehensiveDocUrl || null;
  const existingName = itinerary?.metadata?.comprehensiveDocName || null;
  const existingChars = itinerary?.metadata?.comprehensiveDocChars || null;

  const country = itinerary?.metadata?.country;
  const name = itinerary?.metadata?.name;

  const canUpload = !!(country && name);

  const handleFile = async (file) => {
    if (!canUpload) {
      toast.error('Itinerary must have a country and name before uploading.');
      return;
    }

    const text = await file.text();
    setPreview(text.slice(0, 300));

    // Wrap in JSON so the backend's document uploader accepts it
    const wrapped = JSON.stringify({
      type: 'comprehensive',
      title: file.name,
      charCount: text.length,
      content: text,
    });

    const blob = new Blob([wrapped], { type: 'application/json' });
    const jsonFile = new File([blob], 'comprehensive.json', { type: 'application/json' });

    const pathComponents = getStoragePathComponents({ country, name, language: 'en-us' });
    const storagePath = `special_itineraries/${pathComponents.country}/${pathComponents.folderName}/context/comprehensive.json`;

    const formData = new FormData();
    formData.append('document', jsonFile);
    formData.append('storagePath', storagePath);
    formData.append('country', country);
    formData.append('itineraryName', name);

    setUploading(true);
    try {
      const headers = await getFirebaseUploadAuthHeaders(currentUser);
      const res = await fetch(UPLOAD_URL, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      const data = await res.json();

      // Normalise to direct storage URL
      let url = data.url || '';
      if (url.includes('firebasestorage.googleapis.com/v0/b/')) {
        const u = new URL(url);
        const parts = u.pathname.split('/o/');
        if (parts.length === 2) {
          const bucket = parts[0].replace('/v0/b/', '');
          url = `https://storage.googleapis.com/${bucket}/${decodeURIComponent(parts[1])}`;
        }
      }

      setItinerary(prev => ({
        ...prev,
        metadata: {
          ...(prev?.metadata || {}),
          comprehensiveDocUrl: url,
          comprehensiveDocName: file.name,
          comprehensiveDocChars: text.length,
        },
      }));

      toast.success(`Uploaded "${file.name}" (${text.length.toLocaleString()} chars)`);
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleRemove = () => {
    setPreview('');
    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...(prev?.metadata || {}),
        comprehensiveDocUrl: null,
        comprehensiveDocName: null,
        comprehensiveDocChars: null,
      },
    }));
    toast.success('Comprehensive document removed');
  };

  return (
    <div className="border border-teal-200 rounded-lg bg-teal-50 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-teal-600" />
        <span className="text-sm font-semibold text-teal-800">Comprehensive Context Document</span>
        {existingUrl && <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />}
      </div>

      <p className="text-xs text-teal-700">
        Upload a full document about this place (up to ~100k chars). During block generation the AI
        reads the entire document and extracts what's relevant per topic — no keyword matching needed.
      </p>

      {existingUrl ? (
        <div className="bg-white rounded-md border border-teal-200 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-800">{existingName}</p>
              <p className="text-xs text-gray-400">
                {existingChars?.toLocaleString()} characters
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => inputRef.current?.click()}
                className="text-xs px-2 py-1 rounded border border-teal-300 text-teal-700 hover:bg-teal-50"
              >
                Replace
              </button>
              <button onClick={handleRemove} className="p-1 rounded hover:bg-red-50 text-red-400">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <a href={existingUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-teal-500 hover:underline truncate block">
            {existingUrl}
          </a>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            uploading
              ? 'border-teal-300 bg-teal-50 cursor-wait'
              : 'border-teal-300 hover:border-teal-500 hover:bg-white'
          }`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader className="w-5 h-5 text-teal-500 animate-spin" />
              <p className="text-xs text-teal-600">Uploading…</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-5 h-5 text-teal-400" />
              <p className="text-xs text-teal-700 font-medium">Drop file here or click to browse</p>
              <p className="text-[10px] text-teal-500">.txt · .md · .json</p>
            </div>
          )}
        </div>
      )}

      {preview && !existingUrl && (
        <div className="bg-white rounded border border-teal-200 p-2">
          <p className="text-[10px] text-gray-400 mb-1">Preview</p>
          <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">{preview}…</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
      />
    </div>
  );
};

export default ComprehensiveDocPanel;
